'use client'

import Link from 'next/link'
import Image from 'next/image'
import { ReactNode } from 'react'
import { Button } from '@/components/ui/button'

export interface ServiceFeature {
  title: string
  description: string
  icon?: ReactNode
}

interface ServiceCardProps {
  title: string
  description: string
  features?: ServiceFeature[]
  image?: string
  ctaText?: string
  ctaLink?: string
  className?: string
  variant?: 'default' | 'featured' | 'minimal'
}

export default function ServiceCard({
  title,
  description,
  features,
  image,
  ctaText,
  ctaLink,
  className = '',
  variant = 'default'
}: ServiceCardProps) {
  const baseClasses = "group relative overflow-hidden transition-all duration-300 h-full"
  
  const variantClasses = {
    default: "premium-card hover:bg-white/10 hover:scale-105",
    featured: "premium-card bg-gradient-to-br from-eskerium-blue/20 to-eskerium-gold/20 hover:scale-105 ring-1 ring-eskerium-blue/30",
    minimal: "glass-card hover:bg-white/10"
  }

  const textColor = 'text-white'
  const subtextColor = 'text-white/70'

  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      {/* Image */}
      {image && (
        <div className="relative h-48 overflow-hidden rounded-t-xl">
          <Image
            src={image}
            alt={title}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-eskerium-dark/50 to-transparent"></div>
        </div>
      )}

      {/* Content */}
      <div className="p-6 flex flex-col h-full">
        {/* Title */}
        <h3 className={`text-xl font-semibold mb-3 ${textColor} group-hover:text-eskerium-gold transition-colors`}>
          {title}
        </h3>

        {/* Description */}
        <p className={`mb-6 leading-relaxed ${subtextColor}`}>
          {description}
        </p>

        {/* Features List */}
        {features && features.length > 0 && (
          <div className="mb-6 space-y-3 flex-grow">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                {feature.icon && (
                  <div className={`flex-shrink-0 mt-1 ${variant === 'featured' ? 'text-eskerium-gold' : 'text-eskerium-blue'}`}>
                    {feature.icon}
                  </div>
                )}
                <div>
                  <h4 className={`font-medium mb-1 ${textColor}`}>
                    {feature.title}
                  </h4>
                  <p className={`text-sm ${subtextColor}`}>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Button */}
        {ctaText && ctaLink && (
          <div className="mt-auto">
            <Button 
              asChild 
              variant={variant === 'featured' ? 'premium' : 'outline'} 
              size="lg" 
              className="w-full"
            >
              <Link href={ctaLink}>
                {ctaText}
              </Link>
            </Button>
          </div>
        )}
      </div>

    </div>
  )
}